import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import api from '../utils/api';
import { usePushNotifications } from '../hooks/usePushNotifications';

const roleLabels = {
  admin: 'مدير النظام', sales_manager: 'مدير مبيعات',
  area_supervisor: 'مشرف منطقة', supervisor: 'مشرف', salesman: 'مندوب مبيعات',
};

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const toast    = useToast();
  const navigate = useNavigate();
  const [tab, setTab]     = useState('info');
  const [pw, setPw]       = useState({ current: '', newPw: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  const push = usePushNotifications();

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setInstalled(true));
    if (window.matchMedia('(display-mode: standalone)').matches) setInstalled(true);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function changePassword() {
    if (!pw.current || !pw.newPw || !pw.confirm) { toast.error('يرجى ملء جميع الحقول'); return; }
    if (pw.newPw.length < 8) { toast.error('كلمة المرور يجب أن تكون 8 أحرف على الأقل'); return; }
    if (pw.newPw !== pw.confirm) { toast.error('كلمتا المرور غير متطابقتين'); return; }
    setSaving(true);
    try {
      await api.post('/auth/login', { username: user.username, password: pw.current });
      await api.post(`/users/${user.id}/reset-password`, { password: pw.newPw });
      toast.success('تم تغيير كلمة المرور بنجاح');
      setPw({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      toast.error(err.message === 'Invalid username or password'
        ? 'كلمة المرور الحالية غير صحيحة' : err.message || 'خطأ');
    } finally { setSaving(false); }
  }

  async function togglePush() {
    if (push.subscribed) {
      await push.unsubscribe();
      toast.success('تم إلغاء الاشتراك في الإشعارات');
    } else {
      const result = await push.subscribe();
      if (result.success) {
        toast.success('تم تفعيل الإشعارات بنجاح! 🔔');
      } else if (result.error?.includes('denied')) {
        toast.error('تم رفض الإذن. يرجى تفعيله من إعدادات المتصفح');
      } else {
        toast.error(result.error || 'خطأ في تفعيل الإشعارات');
      }
    }
  }

  async function installApp() {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') { setInstalled(true); setInstallPrompt(null); toast.success('تم تثبيت التطبيق!'); }
  }

  async function handleLogout() { await logout(); navigate('/login'); }

  return (
    <div className="page">
      <Header title="الملف الشخصي" />
      <div className="page-content">

        {/* User Card */}
        <div style={styles.userCard}>
          <div style={styles.bigAvatar}>{user?.name_ar?.charAt(0) || 'م'}</div>
          <h2 style={styles.userName}>{user?.name_ar}</h2>
          <p style={styles.userRole}>{roleLabels[user?.role]}</p>
          <p style={styles.userUsername}>@{user?.username}</p>
        </div>

        {/* Tabs */}
        <div style={styles.tabBar}>
          {[{id:'info',label:'المعلومات'},{id:'password',label:'كلمة المرور'},{id:'app',label:'التطبيق'}].map(t=>(
            <button key={t.id} style={{ ...styles.tab, color:tab===t.id?'var(--primary)':'var(--gray-500)', borderBottom:tab===t.id?'2px solid var(--primary)':'2px solid transparent', fontWeight:tab===t.id?700:400 }} onClick={()=>setTab(t.id)}>{t.label}</button>
          ))}
        </div>

        {/* Info Tab */}
        {tab === 'info' && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              { label:'اسم المستخدم',    value:user?.username },
              { label:'الاسم الكامل',    value:user?.name_ar },
              { label:'الدور الوظيفي',   value:roleLabels[user?.role] },
              { label:'الفرع',           value:user?.branch_name_ar||'—' },
              { label:'NetSuite ID',     value:user?.netsuite_employee_id||'—' },
              { label:'آخر تسجيل دخول', value:user?.last_login_at?new Date(user.last_login_at).toLocaleString('ar-SA'):'—' },
            ].map(item=>(
              <div key={item.label} style={styles.infoRow}>
                <span style={styles.infoLabel}>{item.label}</span>
                <span style={styles.infoValue}>{item.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Password Tab */}
        {tab === 'password' && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={styles.notice}>🔒 يرجى اختيار كلمة مرور قوية لحماية حسابك</div>
            <div className="input-group" style={{margin:0}}>
              <label className="input-label">كلمة المرور الحالية</label>
              <input className="input" type="password" value={pw.current} onChange={e=>setPw(p=>({...p,current:e.target.value}))} placeholder="أدخل كلمة المرور الحالية"/>
            </div>
            <div className="input-group" style={{margin:0}}>
              <label className="input-label">كلمة المرور الجديدة</label>
              <input className="input" type="password" value={pw.newPw} onChange={e=>setPw(p=>({...p,newPw:e.target.value}))} placeholder="8 أحرف على الأقل"/>
            </div>
            <div className="input-group" style={{margin:0}}>
              <label className="input-label">تأكيد كلمة المرور الجديدة</label>
              <input className="input" type="password" value={pw.confirm} onChange={e=>setPw(p=>({...p,confirm:e.target.value}))} placeholder="أعد الإدخال"/>
            </div>
            {pw.newPw && pw.confirm && pw.newPw !== pw.confirm && (
              <p style={{ color:'var(--danger)', fontSize:13 }}>⚠ كلمتا المرور غير متطابقتين</p>
            )}
            <button className="btn btn-primary btn-full btn-lg" onClick={changePassword} disabled={saving}>
              {saving?<><div className="spinner" style={{width:18,height:18,borderWidth:2}}/> جاري الحفظ...</>:'تغيير كلمة المرور'}
            </button>
          </div>
        )}

        {/* App Tab */}
        {tab === 'app' && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

            {/* Push Notifications */}
            <div style={styles.appCard}>
              <div style={styles.appIcon}>🔔</div>
              <div style={{ flex:1 }}>
                <p style={styles.appTitle}>الإشعارات الفورية</p>
                <p style={styles.appDesc}>
                  {push.subscribed
                    ? 'ستصلك إشعارات عند صرف العمولات'
                    : push.supported
                      ? 'فعّل الإشعارات لتصلك تنبيهات العمولات'
                      : 'الإشعارات غير مدعومة في هذا المتصفح'}
                </p>
              </div>
              {push.supported && (
                <button
                  className={`btn btn-sm ${push.subscribed ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={togglePush}
                  disabled={push.loading}
                >
                  {push.loading ? '...' : push.subscribed ? 'إلغاء' : 'تفعيل'}
                </button>
              )}
            </div>

            {/* PWA Install */}
            <div style={styles.appCard}>
              <div style={styles.appIcon}>📱</div>
              <div style={{ flex:1 }}>
                <p style={styles.appTitle}>تثبيت التطبيق</p>
                <p style={styles.appDesc}>أضف التطبيق إلى شاشتك الرئيسية</p>
              </div>
              {installed ? (
                <span style={styles.installedBadge}>✓ مثبت</span>
              ) : installPrompt ? (
                <button className="btn btn-primary btn-sm" onClick={installApp}>تثبيت</button>
              ) : (
                <span style={{ fontSize:12, color:'var(--gray-400)' }}>
                  {window.matchMedia('(display-mode: standalone)').matches ? '✓ مثبت' : 'غير متاح'}
                </span>
              )}
            </div>

            {/* iOS instructions */}
            <div style={styles.appCard}>
              <div style={styles.appIcon}>🍎</div>
              <div style={{ flex:1 }}>
                <p style={styles.appTitle}>تثبيت على iPhone/iPad</p>
                <p style={styles.appDesc}>اضغط على زر المشاركة ← "أضف إلى الشاشة الرئيسية"</p>
              </div>
            </div>

            <div className="divider"/>

            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>الإصدار</span>
              <span style={styles.infoValue}>1.0.0</span>
            </div>

            <div className="divider"/>

            <button className="btn btn-full" style={{ background:'#fee2e2', color:'#991b1b', marginTop:8 }} onClick={handleLogout}>
              تسجيل الخروج
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  userCard: { background:'linear-gradient(135deg,var(--primary-dark),var(--primary))', borderRadius:20, padding:'28px 24px', marginBottom:20, display:'flex', flexDirection:'column', alignItems:'center', gap:10, boxShadow:'var(--shadow-green)' },
  bigAvatar: { width:72, height:72, borderRadius:'50%', background:'rgba(255,255,255,0.2)', border:'3px solid rgba(255,255,255,0.4)', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, fontWeight:800 },
  userName: { fontSize:20, fontWeight:800, color:'white', margin:0 },
  userRole: { fontSize:14, color:'rgba(255,255,255,0.8)', margin:'2px 0 0' },
  userUsername: { fontSize:12, color:'rgba(255,255,255,0.6)', margin:0 },
  tabBar: { display:'flex', background:'white', borderRadius:12, marginBottom:16, boxShadow:'var(--shadow-sm)', border:'1px solid var(--gray-100)', overflow:'hidden' },
  tab: { flex:1, padding:'12px 8px', background:'none', border:'none', fontSize:13, cursor:'pointer', fontFamily:'var(--font-main)', transition:'all 0.2s' },
  infoRow: { background:'white', borderRadius:12, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', boxShadow:'var(--shadow-sm)', border:'1px solid var(--gray-100)' },
  infoLabel: { fontSize:13, color:'var(--gray-500)', fontWeight:500 },
  infoValue: { fontSize:14, fontWeight:600, color:'var(--gray-800)' },
  notice: { background:'var(--primary-light)', borderRadius:12, padding:'12px 16px', fontSize:13, color:'var(--primary-dark)', fontWeight:500 },
  appCard: { background:'white', borderRadius:14, padding:'14px 16px', display:'flex', alignItems:'center', gap:14, boxShadow:'var(--shadow-sm)', border:'1px solid var(--gray-100)' },
  appIcon: { fontSize:28, flexShrink:0 },
  appTitle: { fontSize:14, fontWeight:700, color:'var(--gray-800)', margin:0 },
  appDesc: { fontSize:12, color:'var(--gray-400)', margin:'3px 0 0' },
  installedBadge: { fontSize:12, fontWeight:600, color:'var(--green-700)', background:'var(--green-100)', padding:'4px 10px', borderRadius:20 },
};
